import { createSelector } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { baseApi } from "@/src/store/baseApi";
import type { RootState } from "@/src/store";
import type {
  NormalizedWishlist,
  WishlistItem,
  WishlistResponseApi,
  WishlistToggleResponseApi,
} from "./types";
import { normalizeWishlistResponse } from "./api";

const WISHLIST_TAG = "Wishlist";
const PRODUCT_FALLBACK_IMAGE = "/Office%20Furniture/image-removebg-preview.png";
const EMPTY_WISHLIST_ITEMS: WishlistItem[] = [];

function createOptimisticWishlistItem(variantId: number): WishlistItem {
  return {
    variantId,
    title: "جاري تحديث المنتج...",
    slug: null,
    sku: `variant-${variantId}`,
    price: 0,
    discountPrice: 0,
    discountPercentage: "",
    stock: 1,
    isPublic: true,
    isNew: false,
    image: PRODUCT_FALLBACK_IMAGE,
    hoverImage: PRODUCT_FALLBACK_IMAGE,
    gallery: [PRODUCT_FALLBACK_IMAGE],
    optionValues: [],
  };
}

function applyOptimisticAdd(draft: NormalizedWishlist, variantId: number) {
  const exists = draft.items.some((item) => item.variantId === variantId);

  if (exists) {
    return;
  }

  draft.items.unshift(createOptimisticWishlistItem(variantId));
  draft.updatedAt = new Date().toISOString();
}

function applyOptimisticRemove(draft: NormalizedWishlist, variantId: number) {
  draft.items = draft.items.filter((item) => item.variantId !== variantId);
  draft.updatedAt = new Date().toISOString();
}

function applyOptimisticToggle(draft: NormalizedWishlist, variantId: number) {
  const exists = draft.items.some((item) => item.variantId === variantId);

  if (exists) {
    applyOptimisticRemove(draft, variantId);
    return;
  }

  applyOptimisticAdd(draft, variantId);
}

const wishlistApiWithTags = baseApi.enhanceEndpoints({
  addTagTypes: [WISHLIST_TAG],
});

export const wishlistApi = wishlistApiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getWishlist: builder.query<NormalizedWishlist, void>({
      query: () => ({
        url: "wishlist",
        method: "GET",
      }),
      transformResponse: (response: WishlistResponseApi) =>
        normalizeWishlistResponse(response),
      providesTags: [{ type: WISHLIST_TAG, id: "CURRENT" }],
    }),
    addToWishlist: builder.mutation<
      WishlistToggleResponseApi,
      { variantId: number }
    >({
      async queryFn({ variantId }, _api, _extraOptions, fetchWithBQ) {
        const currentWishlistResult = await fetchWithBQ({
          url: "wishlist",
          method: "GET",
        });

        if (currentWishlistResult.error) {
          return {
            error: currentWishlistResult.error as FetchBaseQueryError,
          };
        }

        const currentWishlist = normalizeWishlistResponse(
          currentWishlistResult.data as WishlistResponseApi,
        );

        if (currentWishlist.items.some((item) => item.variantId === variantId)) {
          return {
            data: {
              added: true,
              message: "المنتج موجود بالفعل في المفضلة",
            },
          };
        }

        const toggleResult = await fetchWithBQ({
          url: "wishlist",
          method: "POST",
          body: { variant_id: variantId },
        });

        if (toggleResult.error) {
          return {
            error: toggleResult.error as FetchBaseQueryError,
          };
        }

        return {
          data: toggleResult.data as WishlistToggleResponseApi,
        };
      },
      async onQueryStarted({ variantId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlist", undefined, (draft) => {
            applyOptimisticAdd(draft, variantId);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: WISHLIST_TAG, id: "CURRENT" }],
    }),
    removeFromWishlist: builder.mutation<
      WishlistToggleResponseApi,
      { variantId: number }
    >({
      async queryFn({ variantId }, _api, _extraOptions, fetchWithBQ) {
        const currentWishlistResult = await fetchWithBQ({
          url: "wishlist",
          method: "GET",
        });

        if (currentWishlistResult.error) {
          return {
            error: currentWishlistResult.error as FetchBaseQueryError,
          };
        }

        const currentWishlist = normalizeWishlistResponse(
          currentWishlistResult.data as WishlistResponseApi,
        );

        if (!currentWishlist.items.some((item) => item.variantId === variantId)) {
          return {
            data: {
              added: false,
              message: "المنتج غير موجود في المفضلة",
            },
          };
        }

        const toggleResult = await fetchWithBQ({
          url: "wishlist",
          method: "POST",
          body: { variant_id: variantId },
        });

        if (toggleResult.error) {
          return {
            error: toggleResult.error as FetchBaseQueryError,
          };
        }

        return {
          data: toggleResult.data as WishlistToggleResponseApi,
        };
      },
      async onQueryStarted({ variantId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlist", undefined, (draft) => {
            applyOptimisticRemove(draft, variantId);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: WISHLIST_TAG, id: "CURRENT" }],
    }),
    toggleWishlist: builder.mutation<
      WishlistToggleResponseApi,
      { variantId: number }
    >({
      query: ({ variantId }) => ({
        url: "wishlist",
        method: "POST",
        body: { variant_id: variantId },
      }),
      async onQueryStarted({ variantId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlist", undefined, (draft) => {
            applyOptimisticToggle(draft, variantId);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: WISHLIST_TAG, id: "CURRENT" }],
    }),
    mergeGuestWishlist: builder.mutation<{ merged: number }, void>({
      query: () => ({
        url: "wishlist/merge",
        method: "POST",
      }),
      invalidatesTags: [{ type: WISHLIST_TAG, id: "CURRENT" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddToWishlistMutation,
  useGetWishlistQuery,
  useLazyGetWishlistQuery,
  useMergeGuestWishlistMutation,
  useRemoveFromWishlistMutation,
  useToggleWishlistMutation,
} = wishlistApi;

const selectWishlistQueryState = wishlistApi.endpoints.getWishlist.select();
const selectWishlistQueryResult = (state: RootState) =>
  selectWishlistQueryState(
    state as unknown as Parameters<typeof selectWishlistQueryState>[0],
  );

export const selectWishlistData = createSelector(
  selectWishlistQueryResult,
  (result) => result.data ?? null,
);

export const selectWishlistItems = createSelector(
  selectWishlistData,
  (wishlist) => wishlist?.items ?? EMPTY_WISHLIST_ITEMS,
);

export const selectWishlistCount = createSelector(
  selectWishlistItems,
  (items) => items.length,
);

export const selectWishlistVariantIdsSet = createSelector(
  selectWishlistItems,
  (items) => new Set(items.map((item) => item.variantId)),
);

export const selectIsVariantInWishlist = (variantId: number) =>
  createSelector(selectWishlistVariantIdsSet, (variantIdsSet) =>
    variantIdsSet.has(variantId),
  );
